import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, FormControl, Box, Button, Card, CardContent, Checkbox, CircularProgress, Container, Dialog,
  Grid, IconButton, ListItemIcon, Menu, MenuItem, Paper, Stack, SvgIcon, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography, TablePagination,
} from '@mui/material';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useSnackbar } from 'components/snackbar';
import { useAuth } from 'hooks/useAuthContext';
import exportFromJSON from 'export-from-json';
import SearchIcon from '@mui/icons-material/Search';
import { getAllJobList, deleteJob, createDuplicateJob, jobUpdated } from 'api/job';
import * as XLSX from "xlsx";
import dayjs from 'dayjs';
import { DuplicateSvgIcon, CopySvgIcon, CloseJobIcon, EditIcon, DeleteIcon, ThreeDotSvg, CloseSvgIcon } from 'components/icon/icon';

const AllJobList = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchParameter, setSearchParameter] = useState(false);
  const [ActionAnchorEl, setActionAnchorEl] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [openCS, setOpenCS] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [openDP, setOpenDP] = useState(false);

  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState('');
  const [degreeData, setDegreeData] = useState([]);
  const [roleData, setRoleData] = useState([]);

  const [skillsList, setSkillsList] = useState([]);
  const [uploadData, setUploadData] = useState("");

  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const defaultValues = {
    jobId: "",
    jobName: "",
    skills: [],
    experience: "",
    salary: "",
    education: [],
    location: '',
    companyName: "",
    function: '',
    role: '',
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { control, reset, handleSubmit, formState: { errors, isSubmitting }, } = methods;

  useEffect(() => {
    setLoading(true);
    fetchJobs();
    fetchDegreeData();
    fetchRoleData();
  }, []);

  /**
   * Fetches the list of jobs from the server and updates the state accordingly.
   * If the response is successful, it updates the jobs state with the received data.
   * If the response fails, it logs an error message to the console and displays a snackbar with an error message.
   * @param {Object} [params={}] - Filters to apply to the job list.
   * @returns {Promise<void>} Resolves when the operation is complete.
   */
  useEffect(() => {
    /**
     * Fetch the list of skills from the server
     */
    const fetchSkills = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_END_POINT}${endpoints?.services?.allSkills}`
        );
        const data = await response.json();
        setSkillsList(data || []);
      } catch (error) {
        console.error("Failed to fetch skills list", error);
      }
    };

    fetchSkills();
  }, []);
  const fetchJobs = async (params = {}) => {
    try {
      const response = await getAllJobList(params);
      // console.log(response);
      if (response?.success && response.data) {
        setJobs(response.data || []);
      } else {
        enqueueSnackbar('Failed to fetch jobs', { variant: 'error' });
        console.error('Failed to fetch jobs:', response?.message);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      enqueueSnackbar('Error fetching jobs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    await fetchJobs(formData);
  });

  /**
   * Downloads the selected job data in a CSV format.
   * @param {Object} job - The job object whose data is to be downloaded.
   * @returns {void}
   */
  const downloadSelectedJobsData = (job) => {
    const selectedJobsData = jobs?.filter(job => selectedJobs?.includes(job?._id));

    const jobList = selectedJobsData.map(job => {
      const experienceStr = job['experience'];
      const experienceArray = experienceStr ? experienceStr.split('-').map(s => s.trim()) : [];
      const min = experienceArray[0] || '0';
      const max = experienceArray[1] || experienceArray[0] || '0';
      const jobDescription = parseHtmlToText(job['description']);
      return {
        'Job id': job['jobid'] || 'N/A',
        'Job status': job['status'] || 'N/A',
        'Date of posting': job?.updatedAt ? dayjs(job?.updatedAt).format('DD MMM, YYYY') : job?.posted_at ?
          dayjs(job?.posted_at).format('DD MMM, YYYY') : job?.createdAt ? dayjs(job?.createdAt).format('DD MMM, YYYY') : 'N/A',
        'Company Id': job['company_id'] || 'N/A',
        'Company Name': job['company'] || 'N/A',
        'Job Summary': job['job_overview'] || 'N/A',
        'Designation': job['role'] || 'N/A',
        'Function': job['function'] || 'N/A',
        'Job Type': job['job_type'] || 'N/A',
        'Working hours': job['job_shift'] || 'N/A',
        'Job format': job['work_mode'] || 'N/A',
        'Location': job['location'] || 'N/A',
        'Number of people reporting': job['direct_reports'] || 'N/A',
        'Job description': jobDescription || 'N/A',
        'Education requirement': job?.qualifications?.join(' | ') || 'N/A',
        'Min experience': min || 'N/A',
        'Max experience': max || 'N/A',
        'Max notice period': job['notice_period'] || 'N/A',
        'Max annual compensation': job['job_type'] === 'Full time' ? job['salary'] : "N/A",
        'Compensation Type': job?.job_type === 'Full time' ? job?.compensation || 'N/A' : job?.compensation_model || 'N/A',
        'Required skills': job?.key_skills?.join(' , ') || 'N/A',
        'Industry Type': job['industry_sector'] || 'N/A',
        // 'Notice period': job['notice_period'] || 'N/A',
        'Company Part-time Hourly rate': job['job_type'] === 'Full time' ? 'N/A' : job['salary'] || 'N/A',
        'Part-time - weekly commitment (hours)': job['weekly_time_commitment'] || 'N/A',
        'Gender preference': job['gender_preference'] || 'N/A',
        'Disability preference': job['disability_preference'] || 'N/A'
      };
    });

    const fileName = `jobList_${Date.now()}`;
    const exportType = exportFromJSON.types.csv;
    exportFromJSON({ data: jobList, fileName, exportType });
  };

  function parseHtmlToText(htmlString) {
    if (!htmlString) return 'N/A';

    let text = htmlString;

    // Replace closing paragraph tags with double newlines
    text = text.replace(/<\/p>/gi, '\n\n');
    // Replace closing list item tags with single newlines
    text = text.replace(/<\/li>/gi, '\n');
    // Replace <br> tags with newlines
    text = text.replace(/<br\s*\/?>/gi, '\n');
    // Remove all remaining HTML tags
    text = text.replace(/<[^>]*>/g, '');
    // Replace &nbsp; with regular space before decoding other entities
    text = text.replace(/&nbsp;/gi, ' ');

    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    text = textarea.value;
    // Replace non-breaking space characters (U+00A0) with regular spaces
    text = text.replace(/\u00A0/g, ' ');
    // Trim leading/trailing whitespace and reduce multiple newlines to max 2
    text = text.trim().replace(/\n{3,}/g, '\n\n');

    return text;
  }

  /**
   * Handles the checkbox change event for a job.
   * If the checkbox is checked, it adds the job id to the selectedJobs state.
   * If the checkbox is unchecked, it removes the job id from the selectedJobs state.
   * @param {object} event - The checkbox change event.
   * @param {string} jobId - The id of the job.
   * @returns {undefined}
   */
  const handleCheckboxChange = (event, jobId) => {
    if (event.target.checked) {
      setSelectedJobs([...selectedJobs, jobId]);
    } else {
      setSelectedJobs(selectedJobs.filter(id => id !== jobId));
    }
  };

  /**
   * Handles the select all checkbox change event.
   * If the checkbox is checked, it sets the selectedJobs state to include all job ids.
   * If the checkbox is unchecked, it clears the selectedJobs state.
   * @param {object} event - The select all checkbox change event.
   */
  const handleSelectAllChange = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedJobs(jobs?.map(job => job?._id));
    } else {
      setSelectedJobs([]);
    }
  };

  /**
   * Handles the edit button click event by stopping the event propagation and navigating to the job form page with the job id as state.
   * @param {React.MouseEvent} e - The event to stop propagation.
   * @param {string} id - The id of the job to be edited.
   * @returns {undefined}
   */
  const handleEditButton = (e, id) => {
    e.stopPropagation();
    navigate(`/job-form`, { state: id })
  }
  /**
   * Toggles the search parameter and resets the search form
   * when the search parameter is true.
   * @returns {undefined}
   */
  const handleSearch = () => {
    setSearchParameter(prevState => !prevState);
    if (searchParameter) {
      setLoading(true);
      reset(defaultValues);
      fetchJobs();
    }
  };

  /**
   * Handles the row click event by stopping the event propagation and navigating to the job applicants page for the selected job if the job status is active.
   * If the job status is not active, it displays an error message as a snackbar.
   * @param {React.MouseEvent} e - The event to stop propagation.
   * @param {object} job - The job object.
   * @returns {undefined}
   */
  const handleRowClick = (e, job) => {
    e.stopPropagation();
    const id = job?._id;
    if (job?.status === 'draft') {
      enqueueSnackbar('You can view only active job details.', { variant: 'error' });
      return;
    }
    navigate(`/job-applicants/${id}`)
  };

  /**
   * Handles the job deletion action by stopping event propagation
   * and opening the confirmation dialog with the job title and id.
   * @param {Object} job - The job object containing the title and id.
   * @param {React.MouseEvent} event - The event to stop propagation.
   * @returns {Promise<void>} - A promise that resolves when the action is complete.
   */

  const handleDeleteJob = async (job, event) => {
    event.stopPropagation();
    setOpenCS(true);
    setTitle(job?.title);
    setCompany(job?.company);
    setSelectedJobId(job?._id)

  }
  /**
   * Handles the duplicate job action by stopping event propagation
   * and opening the duplicate job dialog with the job title and id.
   * @param {React.MouseEvent} event - The event to stop propagation.
   * @param {Object} job - The job object containing the title and id.
   * @returns {undefined}
   */
  const handleDuplicate = (event, job) => {
    event.stopPropagation();
    setOpenDP(true);
    setTitle(job?.title);
    setSelectedJobId(job?._id)
  }
  /**
   * Handles the close event for the delete job confirmation dialog.
   * Closes the dialog by setting the openDeleteConfirmation state to false.
   * @param {React.MouseEvent} event - The event to stop propagation.
   * @returns {undefined}
   */
  const handleCloseCSDialog = (event) => {
    event.stopPropagation();
    setOpenCS(false);
  };
  /**
   * Handles the close event for the duplicate job dialog.
   * Closes the dialog by setting the openDuplicateJob state to false.
   * @param {React.MouseEvent} event - The event to stop propagation.
   * @returns {undefined}
   */
  const handleCloseDPDialog = (event) => {
    event.stopPropagation();
    setOpenDP(false);
  };

  /**
   * Handles the removal of a job by stopping the event propagation and
   * initiating the job deletion process. Displays a success notification
   * if the job is deleted successfully, otherwise shows an error notification.
   * Calls fetchJobs to refresh the job list upon successful deletion.
   * Catches errors during the deletion process and displays a failure notification.
   * Finally, resets the removing state and closes the confirmation dialog.
   * 
   * @param {React.MouseEvent} e - The event to stop propagation.
   * @returns {Promise<void>} - A promise that resolves when the removal process is complete.
   */

  const handleRemove = async (e) => {
    e.stopPropagation();
    setIsRemoving(true);
    try {
      const response = await deleteJob({ id: selectedJobId });
      if (response?.success) {
        enqueueSnackbar('Job deleted successfully', { variant: 'success' });
        await fetchJobs();
      } else {
        enqueueSnackbar('Failed to delete job', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to remove item!', { variant: 'error' });
    } finally {
      setIsRemoving(false);
      setOpenCS(false);
    }
  };
  /**
   * Handles the job action menu by stopping event propagation and setting
   * the action anchor element with the currently clicked element. Also sets
   * the selected job id to the given job id.
   * @param {React.MouseEvent} event - The event to stop propagation.
   * @param {string} jobid - The id of the job to be used for the action menu.
   * @returns {undefined}
   */
  const handleActionMenu = (event, jobid) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedJobId(jobid)
  };
  /**
   * Copies the job URL to the clipboard and displays a notification.
   * If the copy operation is successful, a success notification is shown.
   * If the copy operation fails for any reason, an error notification is shown.
   * @param {string} jobId - The id of the job to be copied.
   * @returns {undefined}
   */
  const handleCopyURL = (jobId) => {
    const url = `${window.location.origin}/jobs/${jobId}`;
    navigator.clipboard.writeText(url).then(() => {
      enqueueSnackbar("Job link copied to clipboard", { variant: 'success' });
    }).catch((error) => {
      enqueueSnackbar('Failed to copy job link.', { variant: 'error' });
    });
  };
  /**
   * Handles the duplication of a job by creating a duplicate job entry and navigating
   * to the job form page with the newly created job ID. It stops event propagation,
   * sets a duplicating state, and resets the state upon completion. If the duplication
   * is successful, it closes the duplicate job dialog and navigates to the job form.
   * If an error occurs during the duplication process, it logs the error and displays
   * an error notification.
   * 
   * @param {object} event - The event object from the duplicate job action.
   * @param {string} jobId - The unique identifier of the job to be duplicated.
   */

  const handleDuplicateJob = async (event, jobId) => {
    event.stopPropagation();
    setIsDuplicating(true);
    try {
      const response = await createDuplicateJob({ jobId });
      if (response?.success && response.data) {
        setActionAnchorEl(null);
        setOpenDP(false);
        navigate(`/job-form`, { state: response?.data?._id })
      }
    } catch (error) {
      console.error('Failed to create duplicate:', error);
      enqueueSnackbar('Failed to create duplicate. Please try again later.', { variant: 'error' });
    } finally {
      setIsDuplicating(false);
    }
  };
  /**
   * Handles the close button action by setting the selected job and opening the close dialog.
   * 
   * @param {React.MouseEvent} e - The event object to stop propagation.
   * @param {object} job - The job object to be set as the selected job.
   * @returns {undefined}
   */

  const handleCloseButton = (e, job) => {
    setSelectedJob(job);
    setOpenCloseDialog(true);
  };
  /**
   * Handles the close event for the close job dialog.
   * Closes the dialog, sets the action anchor element to null, and shows a success notification if the job is successfully closed.
   * If the job is not successfully closed, shows an error notification.
   * @returns {undefined}
   */

  const handleCloseJobStatus = async () => {
    try {
      setIsDuplicating(true);
      const { applicants, ...jobData } = selectedJob;
      jobData.status = 'closed';
      const jobId = jobData._id;
      const response = await jobUpdated({ formData: jobData, job_id: jobId });
      if (response?.success === true && response.data) {
        setOpenCloseDialog(false);
        setActionAnchorEl(null);
        enqueueSnackbar('Job closed successfully', { variant: 'success' });
        fetchJobs();
      }
    } catch (e) {
      enqueueSnackbar('Failed to close job', { variant: 'error' });
      console.error('Failed to close job:', e.message);
    } finally {
      setIsDuplicating(false);
    }
  };
  /**
   * Fetches the roles from the server and updates the role data state.
   * The roles are deduplicated using a Set.
   * @memberof JobList
   * @returns {Promise<void>} - Resolves when the roles are fetched successfully, rejects if there's an error.
   */
  const fetchRoleData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.roles}`);
    const resultData = await response.json();
    setRoleData([...new Set([...resultData])]);
  };

  /**
   * Fetches the degree titles from the server and updates the degreeData state.
   * The degree titles are deduplicated using a Set.
   * @memberof JobList
   * @returns {Promise<void>} - Resolves when the degrees are fetched successfully, rejects if there's an error.
   */
  const fetchDegreeData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.degrees}`);
    const resultData = await response.json();
    setDegreeData([...new Set([...resultData])]);
  };

  /**
   * Handles the file upload event. It reads the uploaded file as
   * a binary string, parses it with XLSX, and stores the parsed data
   * in the `parsedData` state variable. The parsed data is a JSON
   * object with the same structure as the Excel file.
   * @param {Event} e - The file upload event.
   */
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    // ✅ Allowed file extensions
    const allowedExtensions = [".xlsx", ".xls", ".csv"];
    const fileName = uploadedFile.name.toLowerCase();
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidExtension) {
      enqueueSnackbar("Please upload a valid Excel or CSV file.", { variant: 'error' });
      return;
    }

    // ✅ File size check (10 MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024;
    if (uploadedFile.size > maxSize) {
      enqueueSnackbar("File size exceeds 10 MB. Please upload a smaller file.", { variant: 'error' });
      return;
    }

    setFile(uploadedFile);
  };

  /**
   * Handles the form submission event. It checks if the uploadData state
   * and parsedData state are not empty, and if so, it sends a POST request
   * to the server with the type and data as JSON payload. If the request
   * is successful, it displays a success message to the user and clears
   * the file input. If the request fails, it logs the error to the console
   * and displays an error message to the user. If the error is a network
   * or server error, it displays a generic error message. If the error is
   * a payload too large error, it displays a specific error message.
   */
  const handleSubmitUpdate = async () => {
    if (!file) return;

    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("userId", user?.id);

      const response = await fetch(
        `${process.env.REACT_APP_END_POINT}${endpoints?.services?.addJobData}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();
      if (result?.success) {
        fetchJobs();
        setFile(null);
      }
      console.log(result);
      enqueueSnackbar(result?.message || "Update complete.", { variant: result?.success ? "success" : "error" });
    } catch (err) {
      console.error("Update error:", err);
      enqueueSnackbar("Failed to update data.", { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Slice data for pagination
  const paginatedJobs = jobs?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading || !degreeData || !degreeData?.length > 0 || !roleData || !roleData?.length > 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '60px', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  };

  return (
    <FormProvider {...methods}>
      <Container style={{ marginTop: '80px', marginRight: '20px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
            Jobs - TJ Jobs
          </Typography>
          <Stack direction="row" alignItems="center" spacing={3} >
            <Button variant="contained" component="label" disabled={isUploading}>
              Upload Jobs
              <input type="file" hidden onChange={handleFileUpload} marginRight={2} />
            </Button>
            {file && <Typography>{file.name}</Typography>}
            {file && <Button variant="contained" onClick={handleSubmitUpdate} disabled={isUploading}>Submit Jobs</Button>}
          </Stack>
        </Stack>
        {user.user_type !== 'user' && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Button variant="contained" onClick={handleSearch}>
              {searchParameter ? "close search" : "search job"}
            </Button>
            <Box>
              <Button variant="contained" onClick={downloadSelectedJobsData} disabled={selectedJobs?.length === 0}>Download Job List</Button>
              {user.user_type !== 'user' && (
                <Button variant="contained" sx={{ marginLeft: 3 }} onClick={() => navigate('/job-form')}>Create New Job</Button>)}
            </Box>
          </Stack>)}
        <Grid container spacing={2}>
          {(user.user_type !== 'user' && searchParameter) && (
            // Search Parameters
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ padding: '1rem' }}>
                <Typography variant="h6" sx={{ marginBottom: '0.5rem' }}>Search Parameters</Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Stack spacing={2}>
                    <Button type="submit" variant="contained" disabled={isSubmitting}> <SearchIcon /></Button>
                    <Controller
                      control={control}
                      name="jobId"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          fullWidth
                          label="Job ID"
                          variant="outlined"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="jobName"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Job Name"
                          variant="outlined"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="skills"
                      defaultValue={defaultValues?.skills || []}
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          data={skillsList}
                          multiple={true}
                          fullWidth
                          label="Select Expertise/Skills"
                          placeholder="Select Expertise/Skills"
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="experience"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          fullWidth
                          label="Enter Min Experience"
                          variant="outlined"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="salary"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Enter Min Salary"
                          variant="outlined"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="education"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          data={degreeData}
                          label="Select Qualification"
                          displayTextKey="display_name"
                          multiple
                          fullWidth
                          disableClearable
                          onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="location"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.location}`}
                          label="Select Location"
                          fullWidth
                          onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="companyName"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.company}`}
                          multiple={false}
                          fullWidth
                          label="Company Name"
                          displayTextKey="name"
                          freeSolo
                          onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="function"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          data={roleData}
                          freeSolo
                          size="small"
                          displayTextKey="name"
                          label="Select Functions"
                          placeholder="Select Functions"
                          fullWidth
                          onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="role"
                      render={({ field }) => (
                        <TextField
                          size="small"
                          {...field}
                          label="Designation"
                          placeholder="Select Role/Designation"
                          variant="outlined"
                          fullWidth
                          onChange={(e) => {
                            field.onChange(e.target.value);
                          }}
                        />
                      )}
                    />
                  </Stack>
                </form>
              </Paper>
            </Grid>
          )}
          {/* Table */}
          <Grid item xs={12} md={(user.user_type !== 'user' && searchParameter) ? 8 : 12}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', alignItems: 'center' }}><CircularProgress /></Box>
            ) : (
              <Paper elevation={3}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
                  <Table stickyHeader >
                    <TableHead>
                      <TableRow>
                        {user.user_type !== 'user' && (
                          <TableCell><Checkbox checked={selectAll} onChange={handleSelectAllChange} /></TableCell>)}
                        {/* <TableCell>#</TableCell> */}
                        {/* <TableCell>Edit</TableCell> */}
                        <TableCell>Job Id</TableCell>
                        <TableCell>Job</TableCell>
                        <TableCell>Company Name</TableCell>
                        <TableCell>Location</TableCell>
                        {/* <TableCell>Function</TableCell> */}
                        <TableCell>Type</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Education</TableCell>
                        {user.user_type === 'user' && (<TableCell>Status</TableCell>)}
                        {user.user_type != 'user' && <TableCell>Applicants</TableCell>}
                        <TableCell>Status</TableCell>
                        <TableCell></TableCell>
                        <TableCell>Options</TableCell>
                      </TableRow>
                    </TableHead>
                    {/* jobs listing */}
                    <TableBody>
                      {paginatedJobs?.length > 0 ? (paginatedJobs?.map((job, index) => (
                        <TableRow key={job._id || index}>
                          {user.user_type !== 'user' && (
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <Checkbox checked={selectedJobs.includes(job._id)} onChange={(event) => handleCheckboxChange(event, job._id)} />
                            </TableCell>)}
                          <TableCell>{job.jobid || 'N/A'}</TableCell>
                          <TableCell>{job.title || 'N/A'}</TableCell>
                          <TableCell>{job.company || 'N/A'}</TableCell>
                          <TableCell>{job.location || 'N/A'}</TableCell>
                          <TableCell>{job.job_type || 'N/A'}</TableCell>
                          <TableCell>{(job.external_link ? 'External Job' : 'TJ Job') || 'N/A'}</TableCell>
                          <TableCell>{job?.qualifications?.join(' | ') || 'N/A'}
                          </TableCell>
                          {user.user_type === 'user' && (<TableCell>{job.application_status || 'N/A'}</TableCell>)}
                          {user.user_type != 'user' && (<TableCell>{job.total_applicants || 0}</TableCell>)}
                          <TableCell>{job?.status}</TableCell>
                          <TableCell>{job?.status !== 'draft' && <Button onClick={(e) => { handleRowClick(e, job); }}>View</Button>}</TableCell>
                          <TableCell>
                            {['active', 'draft'].includes(job?.status) && (
                              <Box sx={{ display: 'flex' }}>
                                <IconButton
                                  key={job._id || index}
                                  aria-label="more"
                                  aria-controls={openActionMenu === job._id ? 'long-menu' : undefined}
                                  aria-expanded={openActionMenu === job._id ? 'true' : undefined}
                                  aria-haspopup="true"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleActionMenu(event, job._id);
                                    setOpenActionMenu(job._id)
                                  }
                                  }
                                  className="job-action-menu-btn"
                                >
                                  <ThreeDotSvg />
                                </IconButton>
                                <Menu
                                  id="long-menu"
                                  className="job-action-menu"
                                  anchorEl={ActionAnchorEl}
                                  open={openActionMenu == job._id}
                                  onClose={(e) => {
                                    e.stopPropagation();
                                    setOpenActionMenu(null);
                                  }}
                                  sx={{ marginTop: 2 }}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                  }}
                                >
                                  {job?.total_applicants === 0 ?
                                    <MenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditButton(e, job?._id);
                                    }}>
                                      <EditIcon /> Edit
                                    </MenuItem> : ''
                                  }
                                  {job?.total_applicants === 0 ?
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteJob(job, e);
                                      }}
                                    >
                                      <DeleteIcon /> Delete
                                    </MenuItem> :
                                    <MenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      handleCloseButton(e, job);
                                    }}>
                                      <CloseJobIcon /> Close
                                    </MenuItem>
                                  }
                                  {job?.status === 'active' &&
                                    <MenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyURL(job?.jobid);
                                      }}
                                    >
                                      <CopySvgIcon sx={{ marginRight: '10px' }} /> Copy URL
                                    </MenuItem>}
                                  <MenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDuplicate(e, job);
                                    }}
                                  >
                                    <DuplicateSvgIcon /> Duplicate
                                  </MenuItem>
                                  <Dialog
                                    open={openDP}
                                    onClose={(event) => handleCloseDPDialog(event)}
                                    aria-labelledby="alert-dialog-title"
                                    aria-describedby="alert-dialog-description"
                                    className="otp-dialog-wrapper"
                                  >
                                    <Card className="otp-card-wrapper" variant="outlined">
                                      <CardContent>
                                        <IconButton
                                          className="close-dialog-btn"
                                          aria-label="delete"
                                          color="primary"
                                          onClick={(event) => handleCloseDPDialog(event)}
                                        >
                                          <CloseSvgIcon />
                                        </IconButton>
                                        <Box mb={3} sx={{ textAlign: 'center' }}>
                                          <Typography mb={1} className="title" component="h6">
                                            {job?.title} at {job?.company}
                                          </Typography>
                                          <Typography sx={{ textAlign: 'center!important' }} className="otp-sent-txt" component="p">
                                            Are you sure you want to duplicate this Job?
                                          </Typography>
                                        </Box>
                                        <Stack direction="row" alignItems="center" spacing={3}>
                                          <Button fullWidth className="otp-btn" variant="outlined" size="medium" onClick={(event) => handleCloseDPDialog(event)}>
                                            Cancel
                                          </Button>
                                          <Button
                                            fullWidth
                                            className="otp-btn"
                                            variant="contained"
                                            size="medium"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDuplicateJob(e, job?._id);
                                            }}
                                            disabled={isDuplicating}
                                          >
                                            {isDuplicating ? 'Duplicating...' : 'Duplicate'}
                                          </Button>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Dialog>
                                </Menu>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))) : (
                        <TableRow><TableCell colSpan={8}>No jobs </TableCell></TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Component */}
                <TablePagination
                  component="div"
                  count={jobs?.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[20]}
                />
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
      {/* Close Job Dialog */}
      <Dialog
        open={openCloseDialog}
        onClose={() => setOpenCloseDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="otp-dialog-wrapper"
      ><Card className="otp-card-wrapper" variant="outlined">
          <CardContent>
            <IconButton
              className="close-dialog-btn"
              aria-label="delete"
              color="primary"
              onClick={() => setOpenCloseDialog(false)}
            >
              <CloseSvgIcon />
            </IconButton>
            <Box mb={3} sx={{ textAlign: 'center' }}>
              <Typography mb={1} className="title" component="h6">{selectedJob?.title} at {selectedJob?.company}</Typography>
              <Typography sx={{ textAlign: 'center!important' }} className="otp-sent-txt" component="p">
                Are you sure you want to close this Job?
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={3}>
              <Button fullWidth className="otp-btn" variant="outlined" size="medium" onClick={() => setOpenCloseDialog(false)}>
                Cancel
              </Button>
              <Button
                fullWidth
                className="otp-btn"
                variant="contained"
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseJobStatus();
                }}
                disabled={isDuplicating}
              >
                {isDuplicating ? 'Closing...' : 'Confirm'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Dialog>
      {/* Delete Job Dialog */}
      <Dialog
        open={openCS}
        onClose={(event) => handleCloseCSDialog(event)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="otp-dialog-wrapper"
      >
        <Card className="otp-card-wrapper" variant="outlined">
          <CardContent>
            <IconButton
              className="close-dialog-btn"
              aria-label="delete"
              color="primary"
              onClick={(event) => handleCloseCSDialog(event)}
            >
              <CloseSvgIcon />
            </IconButton>
            <Box mb={3} sx={{ textAlign: 'center' }}>
              <Typography mb={1} className="title" component="h6">
                {title} at {company}
              </Typography>
              <Typography sx={{ textAlign: 'center!important' }} className="otp-sent-txt" component="p">
                Are you sure you want to remove this Job?
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={3}>
              <Button fullWidth className="otp-btn" variant="outlined" size="medium" onClick={(event) => handleCloseCSDialog(event)}>
                Cancel
              </Button>
              <Button
                fullWidth
                className="otp-btn"
                variant="contained"
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(e);
                }}
                disabled={isRemoving}
              >
                {isRemoving ? 'Removing...' : 'Remove'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Dialog>
    </FormProvider >
  );
};

export default AllJobList;